import { runtimeConfig } from "@/config/runtime"
import type { Facility } from "@/types/warehouse"
import type {
  YandexLngLat,
  YandexLngLatBounds,
  YandexMapLocation,
  YandexMapsApi,
} from "@/types/yandex-maps"

const SCRIPT_ID = "yandex-maps-v3"

let loadPromise: Promise<YandexMapsApi> | null = null

export function getYandexMapsApiKey(): string | undefined {
  return runtimeConfig.yandexMapsApiKey
}

export function loadYandexMaps(): Promise<YandexMapsApi> {
  const apiKey = getYandexMapsApiKey()
  if (!apiKey) {
    return Promise.reject(
      new Error("Не задан yandexMapsApiKey в config.js"),
    )
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise((resolve, reject) => {
    if (window.ymaps3) {
      window.ymaps3.ready.then(() => resolve(window.ymaps3!)).catch(reject)
      return
    }

    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener("load", () => {
        window.ymaps3?.ready.then(() => resolve(window.ymaps3!)).catch(reject)
      })
      existing.addEventListener("error", () =>
        reject(new Error("Не удалось загрузить Яндекс.Карты")),
      )
      return
    }

    const script = document.createElement("script")
    script.id = SCRIPT_ID
    script.async = true
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`
    script.onload = () => {
      window.ymaps3?.ready.then(() => resolve(window.ymaps3!)).catch(reject)
    }
    script.onerror = () =>
      reject(new Error("Не удалось загрузить Яндекс.Карты"))
    document.head.appendChild(script)
  })

  return loadPromise
}

export function getFacilitiesWithCoordinates(facilities: Facility[]): Facility[] {
  return facilities.filter(
    (facility) =>
      typeof facility.longitude === "number" &&
      typeof facility.latitude === "number",
  )
}

export function facilityToCoordinates(facility: Facility): YandexLngLat {
  return [facility.longitude!, facility.latitude!]
}

export type FacilitiesMapLocationOptions = {
  compact?: boolean
}

function getBoundsFromCoordinates(coordinates: YandexLngLat[]): YandexLngLatBounds {
  let minLat = Infinity
  let minLng = Infinity
  let maxLat = -Infinity
  let maxLng = -Infinity

  for (const [lng, lat] of coordinates) {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

function expandBounds(bounds: YandexLngLatBounds, paddingRatio: number): YandexLngLatBounds {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds
  const lngSpan = Math.max(maxLng - minLng, 0.02)
  const latSpan = Math.max(maxLat - minLat, 0.02)
  const lngPad = lngSpan * paddingRatio
  const latPad = latSpan * paddingRatio

  return [
    [minLng - lngPad, minLat - latPad],
    [maxLng + lngPad, maxLat + latPad],
  ]
}

export function getFacilitiesMapLocation(
  facilities: Facility[],
  options?: FacilitiesMapLocationOptions,
): YandexMapLocation {
  const defaultLocation: YandexMapLocation = {
    center: [37.6173, 55.7558],
    zoom: 10,
  }

  if (facilities.length === 0) {
    return defaultLocation
  }

  const paddingRatio = options?.compact ? 0.45 : 0.25
  const coordinates = facilities.map(facilityToCoordinates)
  const bounds = expandBounds(getBoundsFromCoordinates(coordinates), paddingRatio)

  return { bounds }
}
